import {BehaviorSubject, firstValueFrom, Observable} from "rxjs";
import r2Core, { coreCrypto } from "@core/r2";
import {
    IChatOfficeMessage,
    IChatOfficeMessageUser,
    IOfficeChatBody,
    IOfficeChatRequest,
    IOfficeChatResponse,
    IChatOfficePayload, IChatOfficeMessageHistory, IChatOfficeMessageBot,
} from "./office-chat-v1.types";
import {ChatSettingBase} from "@core/base/chat-setting-base";
import {IChatActionsData} from "@core/base/chat-action-base";
import type {IChatMessageMetadata} from "@listat/r2-core"
import {IOfficeChatSetting} from "@core/office/office-chat-setting/office-chat-setting-v1/office-chat-setting-v1.types";
import type { IChatMessageResult } from "@listat/r2-core";
import { environment } from "@env";
import {logger} from "@services/logger/logger.service";
import { OfficeSessionService } from "@core/office/office-session/office.session.service";
import { HttpClient } from "@angular/common/http";


export class OfficeChatV1 {
    public $messages: BehaviorSubject<IChatOfficeMessage[]> = new BehaviorSubject<IChatOfficeMessage[]>([]);

    protected cryptoService = new coreCrypto.Channel();

    constructor(
        protected httpClient: HttpClient,
        protected settingService: ChatSettingBase,
        protected sessionService: OfficeSessionService
    ) {
        this.subscribeOnSessionInfoUpdate()
    }

    protected get(): Observable<IChatOfficeMessage[]> {
        return this.$messages.asObservable();
    }

    protected async send(
        message: string,
        dataAction: IChatActionsData | null,
        isSaveUserMessage: boolean = true
    ): Promise<IChatMessageResult> {
        const request = await this.doBodyForRequest(message, dataAction);
        logger.log('Body:', request.body.dataOriginal);

        const messageUser: IChatOfficeMessageUser = {
            sender: r2Core.constants.chat.MESSAGE_SENDER.user,
            value: message,
            metadata: request.metadata
        }

        if (isSaveUserMessage) {
            this.write(messageUser);
        }

        const payload = await this.doRequestWithMessage(request.body);

        logger.log('Payload:', payload);
        if (payload.isError) {
            return payload;
        }

        const data = await this.cryptoService.DecryptObject<IOfficeChatResponse>(payload.text);
        const botMessage = this.createBotMessage(data.content, data.is_image);
        this.write(botMessage)
        logger.log('Payload decrypted:', data);
        return payload;
    }

    protected createBotMessage(text: string, isImage: boolean): IChatOfficeMessageBot {
        return {
            sender: r2Core.constants.chat.MESSAGE_SENDER.bot,
            value: text,
            isImage: isImage,
        }
    }

    private subscribeOnSessionInfoUpdate(): void {
        const sessionInfo = this.sessionService.getSession();
        this.cryptoService.ApplySecurityCode(`${ sessionInfo.session_id }_${ sessionInfo.user_id }`)

        this.sessionService.sessionInfo$.subscribe(async (sessionInfo) => {
            if (sessionInfo) {
                await this.cryptoService.ApplySecurityCode(`${ sessionInfo.session_id }_${ sessionInfo.user_id }`)
            }
        });
    }

    private write(message: IChatOfficeMessage): void {
        const value = this.$messages.getValue();
        value.push(message)
        this.$messages.next(value);
    }

    private async doRequestWithMessage(body: IOfficeChatBody): Promise<IChatMessageResult> {
        const timestamp = Date.now();
        const traceID = coreCrypto.uuidV4();

        try {
            const data = await this.fetchWithMessage(body, traceID)
            if (data.success) {
                return {
                    text: data.data,
                    isError: false,
                    traceID,
                    timestamp
                }
            }

            throw new Error(data.error_message);
        } catch (e) {
            const error = {
                text: 'Unknown error',
                isError: true,
                traceID,
                timestamp,
            }

            if (e instanceof Error) {
                error.text = e.message;
                return error
            }

            return error
        }
    }

    private async doBodyForRequest(text: string, dataAction: IChatActionsData | null): Promise<IOfficeChatRequest>{
        const quickAction = this.settingService.getCurrentQuickAction();
        const skill = this.settingService.getCurrentSkill();

        if (quickAction) { this.settingService.setCurrentQuickAction(null); }
        if (skill) { this.settingService.setCurrentSkill(null); }

        let message = text;
        if (quickAction) {
            message = message.length === 0 ? quickAction.value : `${quickAction.value}\n\n${message}`;
        } else if (skill) {
            message = message.length === 0 ? skill.prompt : `${skill.prompt}\n\n${message}`;
        }

        const settingOriginal = this.settingService.getSettingOriginal<IOfficeChatSetting>();
        const setting = this.settingService.getSetting();
        const model = skill?.model || setting.options?.model || settingOriginal.model;

        const body = {
            message,
            ...settingOriginal,
            generate_image: false,
            document: '',
            messages: [] as IChatOfficeMessageHistory[],
            model
        }

        if (dataAction) {
            body.document = dataAction.attachDocument;
            body.generate_image = dataAction.img;
            console.log(dataAction.history)
            body.messages = dataAction.history;
            body.message = dataAction.clipboard ? `${message}\n\n${dataAction.clipboard}` : message;
            body.message = dataAction.selection ? `${body.message}\n\n${dataAction.selection}` : body.message;
        }

        const metadata: IChatMessageMetadata = { model };
        if (quickAction) { metadata.quickAction = quickAction; }
        if (skill) { metadata.skill = { title: skill.title, prompt: skill.prompt }; }

        const data = await this.cryptoService.EncryptObject(body);
        return {
            body: { data, dataOriginal: body },
            metadata
        }
    }

    private fetchWithMessage(data: IOfficeChatBody, traceID: string): Promise<IChatOfficePayload> {
        const sessionInfo = this.sessionService.getSession();
        const options = {headers: {
                'x-hs-request-id': traceID
            }}
        const body = { data: data.data }
        const url = `${environment.office_api_host}/api/send-request/${sessionInfo.session_id}`
        const request = this.httpClient.post<IChatOfficePayload>(url, body, options)

        return firstValueFrom<IChatOfficePayload>(request)
    }
}
